<!-- src/views/HealthandSafety/SafetyHistoryView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">
        
        <!-- 1. MASTER COMMAND HEADER -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#22C55E] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5">
            <ShieldCheckIcon class="h-32 w-32 text-white" />
          </div>
          <div class="relative z-10 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Safety Registry History</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">
                H&S Audit Trail • {{ userStore.userProfile?.firstName }} {{ userStore.userProfile?.lastName }}
              </p>
            </div>
            
            <div class="flex flex-wrap items-center gap-4">
              <router-link :to="{ name: 'dashboard' }" class="bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 shadow-lg">
                 <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Dashboard</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- LOADING STATE -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-32">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mb-6"></div>
          <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest text-center">Synchronising Registry Records...</p>
        </div>

        <!-- EMPTY STATE -->
        <div v-else-if="submissions.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-100 animate-fadeIn shadow-inner">
           <ShieldCheckIcon class="h-12 w-12 text-slate-300 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase text-center">No Records Found</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 max-w-sm mx-auto text-center">
             You have not yet submitted any safety intelligence or incident reports.
           </p>
           <router-link :to="{ name: 'HealthSafetyFormsList' }" class="mt-8 inline-block px-10 py-5 bg-[#20FCF1] text-[#0A1630] rounded-[20px] font-black uppercase text-[10px] tracking-widest shadow-xl hover:scale-105 transition-all">
             Open Safety Forms
           </router-link>
        </div>

        <!-- HISTORY AUDIT LIST -->
        <div v-else class="grid grid-cols-1 gap-6 animate-fadeIn text-left">
          <div 
            v-for="sub in submissions" 
            :key="sub.id" 
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl transition-all overflow-hidden group border-l-8"
            :style="{ borderLeftColor: getFormColor(sub) }"
          >
            <div class="p-6 sm:p-8 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
              
              <div class="flex items-center gap-6 flex-grow text-left">
                <!-- FORM TYPE ICON -->
                <div class="h-20 w-20 bg-[#0A1630] rounded-2xl flex flex-col items-center justify-center shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                  <component :is="getFormIcon(sub)" class="h-8 w-8 text-[#20FCF1]" />
                </div>
                
                <div class="flex-grow min-w-0 text-left">
                  <!-- IDENTIFIER REGISTRY -->
                  <div class="flex flex-wrap items-center gap-3 mb-3 text-left">
                    <span class="bg-[#0A1630] text-[#20FCF1] text-[9px] font-black px-3 py-1 rounded-lg uppercase tracking-widest">
                      {{ sub.humanReadableId || getVal(sub, 'humanReadableId') || 'REGISTRY' }}
                    </span>
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2.5 py-1 rounded border border-slate-100">
                      {{ formatFormName(sub) }}
                    </span>
                  </div>
                  
                  <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-3 text-left">
                    Submitted {{ formatDate(sub.submittedAt || getVal(sub, 'submittedAt')) }}
                  </h3>
                  
                  <div class="flex items-center gap-4 text-left">
                    <div class="flex items-center gap-2">
                       <MapPinIcon class="h-3 w-3 text-slate-300" />
                       <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest truncate">
                         {{ getVal(sub, 'observationLocation') || getVal(sub, 'location') || 'Location Registered' }}
                       </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- STATUS & LINK -->
              <div class="flex flex-col lg:items-end gap-4 w-full lg:w-auto border-t lg:border-t-0 border-slate-50 pt-6 lg:pt-0 text-left lg:text-right">
                <div :class="getStatusStyles(getVal(sub, 'status'))" class="px-8 py-2.5 rounded-full text-[9px] font-black uppercase tracking-[0.15em] border shadow-sm text-center min-w-[140px]">
                  {{ getVal(sub, 'status') || 'Pending Review' }}
                </div>
                
                <router-link 
                  :to="{ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: sub.id } }"
                  class="flex items-center justify-center gap-2 px-8 py-4 bg-[#0A1630] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-xl active:scale-95"
                >
                  <EyeIcon class="h-4 w-4" />
                  <span>Review Full Audit</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { 
  ShieldCheckIcon, ArrowUturnLeftIcon, EyeIcon, 
  MapPinIcon, ClipboardDocumentListIcon, ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const submissions = ref([]);
const loading = ref(true);

/** 🛡️ NESTING AWARENESS HELPER */
const getVal = (sub, key) => {
  if (!sub) return null;
  return sub[key] ?? sub.formData?.[key] ?? null;
};

const formatFormName = (sub) => {
    const id = sub.formIdentifier || getVal(sub, 'formIdentifier');
    if (id === 'observationForm') return 'Safety Observation';
    if (id === 'accidentForm') return 'Accident Report';
    if (id === 'accidentWitnessStatementForm') return 'Witness Statement';
    return 'Safety Record';
};

const getFormIcon = (sub) => {
    const id = sub.formIdentifier || getVal(sub, 'formIdentifier');
    if (id === 'observationForm') return ClipboardDocumentListIcon;
    if (id === 'accidentForm') return ExclamationTriangleIcon;
    if (id === 'accidentWitnessStatementForm') return ChatBubbleLeftRightIcon;
    return ShieldCheckIcon;
};

const getFormColor = (sub) => {
    const id = sub.formIdentifier || getVal(sub, 'formIdentifier');
    if (id === 'observationForm') return '#20FCF1'; // Mint
    if (id === 'accidentForm') return '#EF4444'; // Red
    if (id === 'accidentWitnessStatementForm') return '#6366F1'; // Indigo
    return '#CBD5E1';
};

const getStatusStyles = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('reviewed') || s.includes('completed')) return 'bg-[#EBFDF8] text-[#20FCF1] border-[#20FCF1]/20';
  if (s.includes('action')) return 'bg-rose-50 text-rose-600 border-rose-100';
  return 'bg-amber-50 text-amber-700 border-amber-100'; // Default Pending
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const date = val.toDate ? val.toDate() : new Date(val);
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fetchHistory = async () => {
  loading.value = true;
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    
    const q = query(
      collection(db, 'healthsafetyFormSubmissions'), 
      where('userId', '==', userId), 
      orderBy('submittedAt', 'desc')
    );
    
    const snap = await getDocs(q);
    submissions.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (err) { 
    console.error("Registry Audit Retrieval Error:", err); 
  } finally { 
    loading.value = false; 
  }
};

onMounted(() => { 
  document.title = "Safety Submissions | Sentinel Safe";
  if (userStore.isAuthReady) fetchHistory();
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>